import { apiGet } from './utils';
import type { PagedResponse } from '../types';

export interface RequestAttachment {
  key: string;
  uuid: string;
  user: string;
  request: string;
  message: string | null;
  filename: string;
  content_type: string;
  filesize: number;
  google_cloud_storage_location: string;
  is_shown_to_client: boolean;
  date_created: string;
}

/**
 * Combined attachment that can be either a legacy FHRequestAttachment
 * or a new File+FileAssociation
 */
export interface CombinedAttachment {
  type: 'attachment' | 'file';
  key: string;
  uuid: string | null;
  file_uuid?: string; // only for type='file'
  filename: string;
  content_type: string;
  filesize: number | null;
  date_created: string | null;
}

/**
 * Combined listing response for sidebar attachments
 */
export interface CombinedAttachmentsResponse {
  items: CombinedAttachment[];
}

export const attachmentApi = {
  /**
   * Get a single request attachment
   */
  async get(attachmentKey: string): Promise<RequestAttachment> {
    return apiGet<RequestAttachment>('/request-attachment/', {
      key: attachmentKey,
    });
  },

  /**
   * List attachments for a request
   */
  async listByRequest(
    requestKey: string,
    cursor?: string
  ): Promise<PagedResponse<RequestAttachment>> {
    return apiGet<PagedResponse<RequestAttachment>>(
      '/request-attachment/list/by-request',
      {
        request: requestKey,
        cursor,
      }
    );
  },

  /**
   * Get a signed download URL for a request attachment
   */
  async getSignedUrl(
    attachmentKey: string,
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<{ 'signed-url': string }> {
    return apiGet<{ 'signed-url': string }>(
      '/request-attachment/get-signed-url',
      {
        attachment: attachmentKey,
        disposition,
      }
    );
  },

  /**
   * List all attachments for a request (both legacy FHRequestAttachments
   * and new File+FileAssociation)
   */
  async listCombined(requestKey: string): Promise<CombinedAttachmentsResponse> {
    return apiGet<CombinedAttachmentsResponse>(
      '/request-attachment/list/combined',
      { request: requestKey }
    );
  },
};
